import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, BookOpen, Headphones, PenLine, Mic } from 'lucide-react';
import Navbar from './Navbar';
import { API_BASE } from '../config/api';
import { labelForType } from '../utils/questionTypeStats';

const SKILLS = [
  { key: 'reading', label: 'Reading', unit: 'accuracy', Icon: BookOpen },
  { key: 'listening', label: 'Listening', unit: 'accuracy', Icon: Headphones },
  { key: 'writing', label: 'Writing', unit: 'band', Icon: PenLine },
  { key: 'speaking', label: 'Speaking', unit: 'band', Icon: Mic },
];

const accTone = (a) => (a >= 70 ? 'text-green-600' : a >= 40 ? 'text-amber-600' : 'text-red-500');
const accBadge = (a) => (a >= 70 ? 'bg-green-100 text-green-700' : a >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600');

// Quy đổi tỉ lệ đúng (%) → band tương đối cho Reading/Listening (bảng tỉ lệ IELTS).
const BAND_TABLE = [
  [97.5, 9.0], [92.5, 8.5], [87.5, 8.0], [82.5, 7.5], [75, 7.0], [67.5, 6.5],
  [57.5, 6.0], [47.5, 5.5], [37.5, 5.0], [30, 4.5], [22.5, 4.0], [15, 3.5], [7.5, 3.0], [0, 2.5],
];
const bandFromAccuracy = (pct) => {
  const p = Number(pct) || 0;
  for (const [min, band] of BAND_TABLE) if (p >= min) return band.toFixed(1);
  return '2.5';
};

function TrendBadge({ value, suffix = '%' }) {
  if (value === null || value === undefined) {
    return <span className="inline-flex items-center gap-1 text-gray-400 text-sm"><Minus className="w-4 h-4" /> —</span>;
  }
  if (value > 0) return <span className="inline-flex items-center gap-1 text-green-600 text-sm font-semibold"><TrendingUp className="w-4 h-4" /> +{value}{suffix}</span>;
  if (value < 0) return <span className="inline-flex items-center gap-1 text-red-500 text-sm font-semibold"><TrendingDown className="w-4 h-4" /> {value}{suffix}</span>;
  return <span className="inline-flex items-center gap-1 text-gray-500 text-sm"><Minus className="w-4 h-4" /> 0{suffix}</span>;
}

export default function ResultsOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('reading');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/student/results-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setData(await res.json());
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, [navigate]);

  const skills = data?.skills || {};
  const typeStats = data?.question_type_stats || {};
  const skillMeta = SKILLS.find(s => s.key === active);
  const skill = skills[active] || { items: [], count: 0, average: null, trend: null, unit: skillMeta?.unit };
  const isBand = skillMeta?.unit === 'band';

  // Chart data: build_skill_history gives newest-first → reverse to chronological.
  const chartData = [...(skill.items || [])].reverse().map((it, i) => ({
    name: `#${i + 1}`,
    value: it.value,
  })).filter(d => d.value !== null && d.value !== undefined);

  const rows = typeStats[active] || [];
  const wb = data?.writing_breakdown || { tasks: [], types: [], criteria: {} };
  const speaking = data?.speaking_breakdown || null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#2b5356]">Tổng Quan Kết Quả</h1>
          <p className="text-gray-500 mt-1">Chọn kỹ năng để xem thống kê chi tiết</p>
        </div>

        {/* Skill tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {SKILLS.map(({ key, label, Icon }) => {
            const cnt = skills[key]?.count || 0;
            const on = active === key;
            return (
              <button
                key={key}
                onClick={() => setActive(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  on ? 'bg-[#0096b1] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#0096b1]'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
                <span className={`text-xs rounded-full px-1.5 ${on ? 'bg-white/25' : 'bg-gray-100'}`}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Đang tải...</div>
        ) : active === 'speaking' ? (
          /* Speaking không đo bằng số câu đúng như ba kỹ năng kia mà bằng band, nên có
             khối riêng: điểm trung bình, band từng Part, và 4 tiêu chí IELTS. */
          !speaking || !speaking.attempts ? (
            <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-400">
              Chưa có bài Speaking nào được chấm. Thi một bài rồi bấm chấm để xem thống kê.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl bg-gradient-to-r from-[#2b5356] to-[#1e3c3e] text-white p-6">
                <p className="text-white/70 text-sm font-semibold">Overall Speaking Band trung bình</p>
                <p className="text-5xl font-bold mt-1 tabular-nums">
                  {speaking.overall_avg == null ? '—' : Number(speaking.overall_avg).toFixed(1)}
                </p>
                <p className="text-white/70 text-sm mt-1">
                  {speaking.attempts} bài đã chấm
                  {speaking.latest != null && ` · gần nhất ${Number(speaking.latest).toFixed(1)}`}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-[#2b5356] mb-3">Điểm theo từng Part</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {speaking.parts.map((p) => (
                    <div key={p.label} className="rounded-xl border-2 border-gray-100 px-4 py-3">
                      <p className="text-sm text-gray-500">{p.label}</p>
                      <p className="text-2xl font-bold text-[#0096b1] tabular-nums">
                        {p.avg == null ? '—' : Number(p.avg).toFixed(1)}
                      </p>
                      <p className="text-xs text-gray-400">{p.count} lần</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-[#2b5356] mb-3">Điểm thành phần</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {speaking.criteria.map((c) => (
                    <div key={c.key} className="rounded-xl border-2 border-gray-100 px-4 py-3
                                                flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-[#2b5356]">{c.label}</span>
                      <span className="text-xl font-bold text-[#eb7e37] tabular-nums">
                        {c.avg == null ? '—' : Number(c.avg).toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        ) : (skill.count || 0) === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-400">
            Chưa có dữ liệu {skillMeta?.label}. Hãy làm một bài để xem thống kê.
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white rounded-2xl shadow-sm p-5 text-center">
                <div className="text-xs text-gray-500 mb-1">{isBand ? 'Band trung bình' : 'Tỉ lệ chính xác TB'}</div>
                <div className={`text-3xl font-bold ${isBand ? 'text-[#0096b1]' : accTone(skill.average || 0)}`}>
                  {skill.average != null ? (isBand ? skill.average : `${skill.average}%`) : '—'}
                </div>
                {!isBand && skill.average != null && (
                  <div className="text-xs font-semibold text-[#0096b1] mt-1">≈ Band {bandFromAccuracy(skill.average)}</div>
                )}
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-5 text-center">
                <div className="text-xs text-gray-500 mb-1">Số bài đã làm</div>
                <div className="text-3xl font-bold text-gray-700">{skill.count || 0}</div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-5 text-center">
                <div className="text-xs text-gray-500 mb-1">Xu hướng</div>
                <div className="mt-1.5 flex justify-center">
                  <TrendBadge value={skill.trend} suffix={isBand ? '' : '%'} />
                </div>
              </div>
            </div>

            {/* Progress chart */}
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Biểu Đồ Tiến Độ {skillMeta?.label}</h3>
              {chartData.length >= 2 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <YAxis domain={isBand ? [0, 9] : [0, 100]} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <Tooltip formatter={(v) => (isBand ? `Band ${v}` : `${v}%`)} />
                    <Line type="monotone" dataKey="value" stroke="#0096b1" strokeWidth={2.5} dot={{ r: 3, fill: '#0096b1' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-gray-400 py-10 text-sm">Cần ít nhất 2 lần làm để vẽ biểu đồ tiến độ.</div>
              )}
            </div>

            {/* Per-question-type breakdown (reading/listening only) */}
            {!isBand && (
              <div className="bg-white rounded-2xl shadow-sm p-5">
                <h3 className="font-semibold text-gray-800 mb-3">Chi tiết theo dạng câu hỏi</h3>
                {rows.length === 0 ? (
                  <div className="text-center text-gray-400 py-6 text-sm">
                    Chưa có dữ liệu theo dạng câu hỏi (các đề cần được gán dạng câu hỏi).
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b">
                          <th className="px-3 py-2 font-medium">Dạng câu hỏi</th>
                          <th className="px-3 py-2 font-medium text-center">Đúng / Tổng</th>
                          <th className="px-3 py-2 font-medium text-center">Tỉ lệ chính xác</th>
                          <th className="px-3 py-2 font-medium text-right">Xu hướng</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {rows.map((r) => (
                          <tr key={r.type} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 font-medium text-gray-800">{labelForType(r.type)}</td>
                            <td className="px-3 py-2.5 text-center text-gray-500">{r.correct}/{r.total}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${accBadge(r.accuracy)}`}>
                                {r.accuracy}%
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right"><TrendBadge value={r.trend} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-3 text-xs text-gray-400">
                  Xu hướng = so sánh nửa số bài gần đây với nửa trước đó. Dữ liệu này được chia sẻ với giáo viên của bạn.
                </p>
              </div>
            )}

            {isBand && active === 'writing' && (
              <>
                {/* Component scores (TR/CC/LR/GRA) average */}
                {wb.criteria && (wb.criteria.tr != null || wb.criteria.cc != null || wb.criteria.lr != null || wb.criteria.gra != null) && (
                  <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
                    <h3 className="font-semibold text-gray-800 mb-3">Điểm thành phần (trung bình)</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[['TR', 'tr'], ['CC', 'cc'], ['LR', 'lr'], ['GRA', 'gra']].map(([code, k]) => (
                        <div key={k} className="border border-gray-100 rounded-xl p-4 text-center">
                          <div className="text-xs font-bold text-gray-400 mb-1">{code}</div>
                          <div className="text-2xl font-extrabold text-[#0096b1]">{wb.criteria[k] != null ? wb.criteria[k] : '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Task 1 / Task 2 band breakdown */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Chi tiết theo Task</h3>
                  {wb.tasks.length === 0 ? (
                    <div className="text-center text-gray-400 py-6 text-sm">Chưa có dữ liệu theo Task.</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {wb.tasks.map((t) => (
                        <div key={t.part} className="border border-gray-100 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-gray-700">{t.label}</span>
                            <span className="text-xs text-gray-400">{t.count} bài</span>
                          </div>
                          <div className="flex items-end justify-between">
                            <div className="text-3xl font-bold text-[#0096b1]">{t.average != null ? t.average : '—'}</div>
                            <TrendBadge value={t.trend} suffix="" />
                          </div>
                          <div className="text-xs text-gray-400 mt-1">Band trung bình</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Per-question-type band breakdown (from forecast question-type tags) */}
                <div className="bg-white rounded-2xl shadow-sm p-5">
                  <h3 className="font-semibold text-gray-800 mb-3">Chi tiết theo dạng đề</h3>
                  {wb.types.length === 0 ? (
                    <div className="text-center text-gray-400 py-6 text-sm">
                      Chưa có dữ liệu theo dạng đề (các đề writing cần được gán dạng câu hỏi).
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b">
                            <th className="px-3 py-2 font-medium">Dạng đề</th>
                            <th className="px-3 py-2 font-medium text-center">Số bài</th>
                            <th className="px-3 py-2 font-medium text-center">Band TB</th>
                            <th className="px-3 py-2 font-medium text-right">Xu hướng</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {wb.types.map((r) => (
                            <tr key={r.type} className="hover:bg-gray-50">
                              <td className="px-3 py-2.5 font-medium text-gray-800">{labelForType(r.type)}</td>
                              <td className="px-3 py-2.5 text-center text-gray-500">{r.count}</td>
                              <td className="px-3 py-2.5 text-center">
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-[#0096b1]/10 text-[#0096b1]">
                                  {r.average != null ? r.average : '—'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-right"><TrendBadge value={r.trend} suffix="" /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="mt-3 text-xs text-gray-400">
                    Band theo dạng đề dựa trên nhãn dạng câu hỏi được gán cho từng đề. Dữ liệu này được chia sẻ với giáo viên của bạn.
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
